import { useState, useEffect } from "react";
import { Heart, MessageCircle, Send } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function EngagementSection({ ebookId, chapterId }) {
  const [likes, setLikes] = useState(0);
  const [liked, setLiked] = useState(false);
  const [comments, setComments] = useState([]);
  const [showComments, setShowComments] = useState(false);
  const [name, setName] = useState(() => localStorage.getItem("commenter_name") || "");
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLiked(false);
    setShowComments(false);
    fetchLikes();
    fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterId]);

  const fetchLikes = async () => {
    try {
      const res = await axios.get(`${API}/api/ebooks/${ebookId}/chapters/${chapterId}/likes`);
      setLikes(res.data.likes);
    } catch (e) { /* silent */ }
  };

  const fetchComments = async () => {
    try {
      const res = await axios.get(`${API}/api/ebooks/${ebookId}/chapters/${chapterId}/comments`);
      setComments(res.data);
    } catch (e) { /* silent */ }
  };

  const handleLike = async () => {
    if (liked) return;
    try {
      const res = await axios.post(`${API}/api/ebooks/${ebookId}/chapters/${chapterId}/like`);
      setLikes(res.data.likes);
      setLiked(true);
    } catch (e) { /* silent */ }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!name.trim() || !content.trim()) return;
    setSubmitting(true);
    try {
      localStorage.setItem("commenter_name", name.trim());
      const res = await axios.post(`${API}/api/ebooks/${ebookId}/chapters/${chapterId}/comments`, {
        name: name.trim(),
        content: content.trim()
      });
      setComments((prev) => [res.data, ...prev]);
      setContent("");
    } catch (e) { /* silent */ }
    setSubmitting(false);
  };

  const timeAgo = (ts) => {
    const diff = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div className="mt-12 pt-8 border-t border-[#E5E5E5]" data-testid="engagement-section">
      {/* Like + Comment toggle */}
      <div className="flex items-center gap-6">
        <button
          onClick={handleLike}
          data-testid="like-button"
          className={`flex items-center gap-2 px-4 py-2 border transition-colors ${
            liked
              ? "border-[#FF4F00] bg-[#FF4F00]/5 text-[#FF4F00]"
              : "border-[#E5E5E5] text-[#666666] hover:border-[#0A0A0A] hover:text-[#0A0A0A]"
          }`}
        >
          <Heart size={16} className={liked ? "fill-[#FF4F00]" : ""} />
          <span className="text-sm font-medium font-mono-plex">{likes}</span>
        </button>

        <button
          onClick={() => setShowComments(!showComments)}
          data-testid="toggle-comments-button"
          className={`flex items-center gap-2 px-4 py-2 border transition-colors ${
            showComments
              ? "border-[#0A0A0A] text-[#0A0A0A]"
              : "border-[#E5E5E5] text-[#666666] hover:border-[#0A0A0A] hover:text-[#0A0A0A]"
          }`}
        >
          <MessageCircle size={16} />
          <span className="text-sm font-medium font-mono-plex">
            {comments.length} {comments.length === 1 ? "comment" : "comments"}
          </span>
        </button>
      </div>

      {/* Comments Section */}
      <AnimatePresence>
        {showComments && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="mt-8" data-testid="comments-section">
              <h3 className="font-cabinet font-bold text-lg text-[#0A0A0A] mb-6">Discussion</h3>

              {/* Comment form */}
              <form onSubmit={handleComment} className="mb-8" data-testid="comment-form">
                <div className="flex flex-col sm:flex-row gap-3 mb-3">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    data-testid="comment-name-input"
                    className="flex-1 px-4 py-2.5 border border-[#E5E5E5] bg-white text-sm font-plex focus:outline-none focus:border-[#0A0A0A] transition-colors placeholder:text-[#999]"
                  />
                </div>
                <div className="flex gap-3">
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Share your thoughts on this chapter..."
                    rows={3}
                    data-testid="comment-content-input"
                    className="flex-1 px-4 py-2.5 border border-[#E5E5E5] bg-white text-sm font-plex leading-relaxed resize-none focus:outline-none focus:border-[#0A0A0A] transition-colors placeholder:text-[#999]"
                  />
                </div>
                <div className="flex justify-end mt-3">
                  <button
                    type="submit"
                    disabled={submitting || !name.trim() || !content.trim()}
                    data-testid="submit-comment-button"
                    className="bg-[#0A0A0A] text-white px-5 py-2.5 text-sm font-medium flex items-center gap-2 hover:bg-[#333] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Send size={14} />
                    {submitting ? "Posting..." : "Post Comment"}
                  </button>
                </div>
              </form>

              {/* Comments list */}
              {comments.length === 0 ? (
                <p className="text-sm text-[#666666] font-plex py-6 text-center border-t border-[#E5E5E5]">
                  No comments yet. Be the first to share your thoughts.
                </p>
              ) : (
                <div className="space-y-0">
                  {comments.map((c) => (
                    <div key={c.id} className="py-5 border-t border-[#E5E5E5]" data-testid={`comment-${c.id}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-7 h-7 bg-[#0A0A0A] flex items-center justify-center shrink-0">
                          <span className="text-white text-[10px] font-bold font-mono-plex">
                            {c.name?.charAt(0)?.toUpperCase() || "?"}
                          </span>
                        </div>
                        <span className="font-cabinet font-semibold text-sm text-[#0A0A0A]">{c.name}</span>
                        <span className="text-xs text-[#999] font-mono-plex">{timeAgo(c.timestamp)}</span>
                      </div>
                      <p className="text-sm text-[#0A0A0A] leading-relaxed font-plex ml-10">{c.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
